/**
 * AuthContext — manages JWT storage, user state, and role helpers.
 * Token is stored in localStorage (simple, works for MVP).
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { authApi } from "../utils/api";
import toast from "react-hot-toast";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("tutor_token"));
  const [loading, setLoading] = useState(true);

  // On mount — verify stored token and load user
  useEffect(() => {
    const storedUser = localStorage.getItem("tutor_user");
    if (token && storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch {
        _clear();
      }
    }
    setLoading(false);
  }, [token]);

  const login = useCallback(async (email, password) => {
    const res = await authApi.login(email, password);
    const { access_token, user: userData } = res.data;
    localStorage.setItem("tutor_token", access_token);
    localStorage.setItem("tutor_user", JSON.stringify(userData));
    setToken(access_token);
    setUser(userData);
    return userData;
  }, []);

  const signup = useCallback(async (name, email, password, role, lang = "en") => {
    const res = await authApi.signup(name, email, password, role, lang);
    const { access_token, user: userData } = res.data;
    localStorage.setItem("tutor_token", access_token);
    localStorage.setItem("tutor_user", JSON.stringify(userData));
    setToken(access_token);
    setUser(userData);
    return userData;
  }, []);

  const logout = useCallback(() => {
    _clear();
    setToken(null);
    setUser(null);
  }, []);

  const updateUser = useCallback((updated) => {
    setUser(updated);
    localStorage.setItem("tutor_user", JSON.stringify(updated));
  }, []);

  function _clear() {
    localStorage.removeItem("tutor_token");
    localStorage.removeItem("tutor_user");
  }

  const isAuthenticated = Boolean(token && user);
  const hasRole = (...roles) => roles.includes(user?.role);

  // Apply role class to <html> for theme switching + update favicon
  useEffect(() => {
    const html = document.documentElement;
    html.classList.remove("role-teacher", "role-student");

    const favicon      = document.getElementById("favicon");
    const faviconApple = document.getElementById("favicon-apple");

    if (user?.role === "teacher") {
      html.classList.add("role-teacher");
      if (favicon)      favicon.href      = "/favicon-teacher.png";
      if (faviconApple) faviconApple.href = "/favicon-teacher.png";
    } else if (user?.role === "student") {
      html.classList.add("role-student");
      if (favicon)      favicon.href      = "/favicon.png";
      if (faviconApple) faviconApple.href = "/favicon.png";
    } else {
      // Logged out — default favicon
      if (favicon)      favicon.href      = "/favicon.png";
      if (faviconApple) faviconApple.href = "/favicon.png";
    }
  }, [user?.role]);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, signup, logout, updateUser, isAuthenticated, hasRole }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
